import { existsSync } from 'node:fs';
import { readFile } from 'node:fs/promises';
import { join } from 'node:path';

import { createHash } from 'node:crypto';

import type { StackSnapshot } from '@/core/types/introspection.js';
import type {
  PentestFinding,
  PentestRetestFinding,
  PentestRetestStatus,
  PentestRuntimeStatus,
} from '@/core/types/pentest.js';

import type { OsvVulnerabilityRecord } from './osv.js';
import type { ModuleDocEvidence, SuspiciousRoute, TestEvidence } from './shared.js';
import { extractRelevantTestPaths, parseSecretMatches } from './shared.js';

function createFinding(input: Omit<PentestFinding, 'id'>): PentestFinding {
  return {
    id: '',
    ...input,
  };
}

export async function buildDependencyFindings(
  snapshot: StackSnapshot,
  artifactDir: string,
  osvFindings: OsvVulnerabilityRecord[],
): Promise<PentestFinding[]> {
  const findings: PentestFinding[] = [];
  const nativeFindings = await readNativeAuditFindings(artifactDir);
  const seen = new Set<string>();

  for (const finding of [...nativeFindings, ...osvFindings]) {
    const pkg = snapshot.packages.find((candidate) => candidate.name === finding.package_name);
    const key = `${finding.package_name}:${finding.advisory_id}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    findings.push(
      createFinding({
        title: `Dependency advisory for ${finding.package_name}`,
        description: finding.summary,
        impact: normalizeImpact(`${finding.summary}\n${finding.details}`),
        effort: 'medium',
        possible_solution_direction:
          pkg === undefined
            ? 'Review the dependency and update or replace it if the advisory is relevant.'
            : `Upgrade ${finding.package_name} from ${pkg.locked_version} to a patched release and validate downstream compatibility.`,
        how_to_reproduce: [
          'Run the dependency audit scripts generated by paqad-ai.',
          `Inspect advisories for ${finding.package_name} in the pentest dependency artifacts.`,
        ],
        impact_area: [`package:${finding.package_name}`],
        evidence: [
          `Advisory: ${finding.advisory_id}`,
          `Package: ${finding.package_name}@${finding.version}`,
          finding.details || finding.summary,
        ],
        category: 'dependency-advisory',
        status: 'open',
        confidence: 0.9,
        source_types: ['advisory', 'code'],
        affected_modules: [],
        affected_packages: [finding.package_name],
        runtime_required: false,
        manual_follow_up: false,
      }),
    );
  }

  return findings;
}

export function buildModuleFindings(
  docs: ModuleDocEvidence[],
  tests: TestEvidence[],
): PentestFinding[] {
  const findings: PentestFinding[] = [];

  for (const moduleDoc of docs) {
    const lowered = moduleDoc.content.toLowerCase();

    const permissionTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'forbidden',
      'unauthor',
      'permission',
      'policy',
      'tenant',
      'role',
      '403',
    ]);
    if (hasAny(lowered, ['role', 'permission', 'admin', 'owner', 'tenant', 'authorize'])) {
      if (permissionTests.length === 0) {
        findings.push(
          createFinding({
            title: `Permission boundary evidence is thin for ${moduleDoc.module}`,
            description:
              'Module docs describe privileged or tenant-sensitive behavior, but the current test inventory does not show matching authorization or boundary assertions.',
            impact: 'medium',
            effort: 'medium',
            possible_solution_direction:
              'Add explicit authorization, policy, tenant-isolation, and forbidden-path tests for the privileged flows described in the module docs.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for role or tenant-sensitive actions.`,
              'Search the current tests for authorization, forbidden, tenant, and policy assertions for that module.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching auth boundary test evidence found for this module.',
            ],
            category: 'permission-boundary',
            status: 'open',
            confidence: 0.72,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: false,
          }),
        );
      }
    }

    const stateTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'state',
      'transition',
      'approve',
      'reject',
      'cancel',
      'retry',
      'idempot',
    ]);
    if (hasAny(lowered, ['state', 'transition', 'workflow', 'approval', 'approve', 'reject'])) {
      if (stateTests.length === 0) {
        findings.push(
          createFinding({
            title: `Business workflow abuse coverage is thin for ${moduleDoc.module}`,
            description:
              'The module docs describe stateful or approval-based flows, but the current tests do not show evidence for invalid transition, duplicate action, or workflow bypass protection.',
            impact: 'medium',
            effort: 'medium',
            possible_solution_direction:
              'Add tests for invalid transitions, duplicate submissions, retry safety, and approval bypass attempts for the documented workflow states.',
            how_to_reproduce: [
              `Inspect ${moduleDoc.paths.join(', ')} for workflow or state descriptions.`,
              'Search the current tests for invalid transition, duplicate action, or workflow-bypass coverage.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching state-machine or workflow-bypass tests found for this module.',
            ],
            category: 'business-logic-abuse',
            status: 'open',
            confidence: 0.69,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: false,
          }),
        );
      }
    }

    const criticalActionTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'payment',
      'checkout',
      'refund',
      'idempot',
      'duplicate',
      'race',
      'concurrent',
    ]);
    if (
      hasAny(lowered, ['payment', 'checkout', 'refund', 'transfer', 'payout', 'delete', 'export'])
    ) {
      if (criticalActionTests.length === 0) {
        findings.push(
          createFinding({
            title: `High-value flow abuse checks are missing for ${moduleDoc.module}`,
            description:
              'The module docs reference high-value actions such as payments, exports, or destructive operations, but the current tests do not show abuse-focused validation for duplicate, replay, or concurrency scenarios.',
            impact: 'high',
            effort: 'high',
            possible_solution_direction:
              'Add abuse tests for replay, double-submit, race conditions, and privilege escalation around the documented high-value actions.',
            how_to_reproduce: [
              `Inspect ${moduleDoc.paths.join(', ')} for high-value actions.`,
              'Search the tests for replay, double-submit, concurrency, and race-condition coverage for the same module.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching high-value abuse or idempotency tests found for this module.',
            ],
            category: 'business-logic-abuse',
            status: 'open',
            confidence: 0.76,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: true,
          }),
        );
      }
    }

    // Block 4 — Auth mechanism gap
    // Requires at least 2 keyword matches to reduce false positives from broad terms
    const authMechanismTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'algorithm',
      'expir',
      'signature',
      'refresh',
      'revok',
      'logout',
      'session.invalidat',
      'alg:none',
      'jwt',
    ]);
    if (
      hasAtLeast(lowered, 2, [
        'jwt',
        'token',
        'bearer',
        'session',
        'cookie',
        'oauth',
        'saml',
        'oidc',
        'sanctum',
      ])
    ) {
      if (authMechanismTests.length === 0) {
        findings.push(
          createFinding({
            title: `Auth token lifecycle checks are thin for ${moduleDoc.module}`,
            description:
              'Module docs describe authentication tokens, sessions, or OAuth flows, but the current tests do not show evidence of token expiry, signature validation, session invalidation, or logout handling.',
            impact: 'high',
            effort: 'medium',
            possible_solution_direction:
              'Add tests for token expiry enforcement, alg:none rejection, session invalidation on logout, and refresh token rotation for the documented auth flows.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for token or session handling.`,
              'Search tests for algorithm, expiry, signature, revocation, and logout coverage for that module.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching auth token lifecycle test evidence found for this module.',
            ],
            category: 'auth-mechanism',
            status: 'open',
            confidence: 0.71,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: true,
          }),
        );
      }
    }

    // Block 5 — SSRF / open-redirect surface
    const ssrfTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'ssrf',
      'redirect',
      'allowlist',
      'block',
      'internal',
      'private',
      'localhost',
      'metadata',
    ]);
    if (
      hasAtLeast(lowered, 2, [
        'url',
        'redirect',
        'callback',
        'webhook',
        'fetch',
        'http',
        'proxy',
        'curl',
        'axios',
      ])
    ) {
      if (ssrfTests.length === 0) {
        findings.push(
          createFinding({
            title: `Outbound request / redirect surface lacks abuse validation for ${moduleDoc.module}`,
            description:
              'Module docs describe outbound HTTP calls, URL parameters, webhooks, or redirect flows, but the current tests do not show evidence of SSRF prevention, redirect allowlist validation, or private-range blocking.',
            impact: 'high',
            effort: 'medium',
            possible_solution_direction:
              'Add tests that verify SSRF prevention (blocked private ranges, redirect chain validation) and open-redirect protection for the documented outbound request surfaces.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for URL, redirect, or webhook parameter handling.`,
              'Test with SSRF payloads: 127.0.0.1, 169.254.169.254, [::1], redirect chain to internal IP.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching SSRF or redirect validation test evidence found for this module.',
            ],
            category: 'input-validation',
            status: 'open',
            confidence: 0.7,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: true,
          }),
        );
      }
    }

    // Block 6 — Mass assignment surface
    const massAssignTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'mass',
      'assign',
      'fillable',
      'guarded',
      'allowlist',
      'forbidden',
      'attr_protected',
      'protected',
    ]);
    if (
      hasAtLeast(lowered, 2, [
        'fill',
        'create',
        'update',
        'patch',
        'assign',
        'body',
        'request',
        'fillable',
        'guarded',
      ])
    ) {
      if (massAssignTests.length === 0) {
        findings.push(
          createFinding({
            title: `Mass assignment protection evidence is absent for ${moduleDoc.module}`,
            description:
              'Module docs describe model creation or update flows accepting request body fields, but the current tests do not show evidence of mass assignment protection (fillable allowlist, guarded fields, or forbidden-field rejection).',
            impact: 'medium',
            effort: 'low',
            possible_solution_direction:
              'Verify the model uses an explicit $fillable allowlist or $guarded blocklist. Add tests that confirm unintended fields (is_admin, role, price, balance) are rejected when submitted in the request body.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for model create/update operations.`,
              'Submit a PATCH/POST request with extra privileged fields: is_admin=true, role=admin, price=0.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching mass assignment protection test evidence found for this module.',
            ],
            category: 'input-validation',
            status: 'open',
            confidence: 0.68,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: false,
          }),
        );
      }
    }

    // Block 7 — Cryptographic weakness surface
    const cryptoTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'crypto',
      'encrypt',
      'hash.strength',
      'key.rotation',
      'salt',
      'entropy',
      'randomBytes',
      'bcrypt',
      'argon',
    ]);
    if (
      hasAtLeast(lowered, 2, [
        'encrypt',
        'decrypt',
        'hash',
        'md5',
        'sha1',
        'secret',
        'key',
        'cipher',
        'aes',
        'rsa',
        'hmac',
      ])
    ) {
      if (cryptoTests.length === 0) {
        findings.push(
          createFinding({
            title: `Cryptographic implementation lacks verification coverage for ${moduleDoc.module}`,
            description:
              'Module docs describe encryption, hashing, or key management, but the current tests do not show evidence of cryptographic strength validation (algorithm checks, key rotation, salt uniqueness, or secure PRNG usage).',
            impact: 'high',
            effort: 'medium',
            possible_solution_direction:
              'Add tests that verify password hashing uses bcrypt/argon2 with a sufficient cost factor, encryption uses a secure algorithm with a randomly generated IV, and tokens are generated with a cryptographically secure PRNG.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for cryptographic operations.`,
              'Check for MD5/SHA1 password hashing, hardcoded IVs, or Math.random() for token generation.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching cryptographic strength or key management test evidence found for this module.',
            ],
            category: 'cryptographic',
            status: 'open',
            confidence: 0.67,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: true,
          }),
        );
      }
    }

    // Block 8 — Logging / audit gap
    const loggingTests = extractRelevantTestPaths(tests, moduleDoc.module, [
      'audit',
      'log.assert',
      'event.dispatch',
      'trail',
      'activity',
      'log.entry',
      'logged',
      'event',
    ]);
    if (
      hasAtLeast(lowered, 2, [
        'log',
        'audit',
        'event',
        'track',
        'monitor',
        'alert',
        'sentry',
        'activity',
      ])
    ) {
      if (loggingTests.length === 0) {
        findings.push(
          createFinding({
            title: `Security-relevant actions lack audit logging evidence for ${moduleDoc.module}`,
            description:
              'Module docs describe operations that should be audited (authentication, financial transactions, admin actions, or data exports), but the current tests do not show evidence of audit log assertions or event dispatch verification.',
            impact: 'medium',
            effort: 'medium',
            possible_solution_direction:
              'Add tests that assert audit log entries are written for high-value actions with actor identity, IP, and timestamp. Verify sensitive data (passwords, tokens) is not included in log output.',
            how_to_reproduce: [
              `Review ${moduleDoc.paths.join(', ')} for operations that must be auditable.`,
              'Check logging configuration and assert that log entries are created with sufficient context for forensic analysis.',
            ],
            impact_area: [...moduleDoc.paths, `module:${moduleDoc.module}`],
            evidence: [
              ...moduleDoc.paths.map((path) => `Doc evidence: ${path}`),
              'No matching audit logging or event dispatch test evidence found for this module.',
            ],
            category: 'logging-monitoring',
            status: 'open',
            confidence: 0.65,
            source_types: ['docs', 'tests'],
            affected_modules: [moduleDoc.module],
            affected_packages: [],
            runtime_required: false,
            manual_follow_up: false,
          }),
        );
      }
    }
  }

  return findings;
}

export async function buildSecretFindings(artifactDir: string): Promise<PentestFinding[]> {
  const path = join(artifactDir, 'secrets', 'matches.txt');
  if (!existsSync(path)) {
    return [];
  }

  const matches = parseSecretMatches(await readFile(path, 'utf8'));
  return matches.map((match, index) =>
    createFinding({
      title: `Potential secret exposure match ${index + 1}`,
      description:
        'A generated secret scan found a suspicious credential or secret-like pattern in tracked project files.',
      impact: match.includes('PRIVATE KEY') ? 'high' : 'medium',
      effort: 'low',
      possible_solution_direction:
        'Verify whether the matched value is a real secret, rotate it if needed, and move sensitive data to environment or secret-manager storage.',
      how_to_reproduce: [
        'Run the project secret scan script and inspect the emitted matches.',
        'Confirm whether the matched value is real, test data, or a false positive.',
      ],
      impact_area: [match.split(':').slice(0, 2).join(':')],
      evidence: [match],
      category: 'secret-exposure',
      status: 'open',
      confidence: 0.66,
      source_types: ['code'],
      affected_modules: [],
      affected_packages: [],
      runtime_required: false,
      manual_follow_up: true,
    }),
  );
}

export function buildRuntimeFindings(
  targetUrl: string | null,
  runtimeStatus: PentestRuntimeStatus,
  suspiciousRoutes: SuspiciousRoute[],
): PentestFinding[] {
  if (
    targetUrl === null ||
    runtimeStatus.status === 'blocked' ||
    runtimeStatus.status === 'not-configured'
  ) {
    return [];
  }

  return suspiciousRoutes.map((route) =>
    createFinding({
      title: `Suspicious runtime surface exposed at ${route.path}`,
      description:
        route.kind === 'debug-surface'
          ? 'A debug or observability endpoint appears reachable on the running local application.'
          : route.kind === 'sensitive-file'
            ? 'A sensitive file path appears reachable on the running local application.'
            : 'An admin or high-value runtime surface appears reachable on the running local application.',
      impact: route.kind === 'sensitive-file' ? 'high' : 'medium',
      effort: 'medium',
      possible_solution_direction:
        route.kind === 'debug-surface'
          ? 'Restrict the debug surface to local-only or authenticated access and verify it is disabled outside development.'
          : 'Restrict the route with authentication, authorization, or environment gating.',
      how_to_reproduce: [
        `Ensure the project is running locally at ${targetUrl}.`,
        `Request ${targetUrl}${route.path} and verify the observed status ${route.status}.`,
      ],
      impact_area: [`runtime:${route.path}`],
      evidence: [`Runtime probe observed ${route.path} with HTTP ${route.status}.`],
      category: 'runtime-surface',
      status: 'open',
      confidence: 0.82,
      source_types: ['runtime'],
      affected_modules: [],
      affected_packages: [],
      runtime_required: true,
      manual_follow_up: false,
    }),
  );
}

export function assignFindingIds<T extends PentestFinding>(findings: T[]): T[] {
  const seen = new Map<string, number>();

  return findings.map((finding) => {
    const fingerprint = findingFingerprint(finding);
    const occurrence = (seen.get(fingerprint) ?? 0) + 1;
    seen.set(fingerprint, occurrence);

    return {
      ...finding,
      id:
        occurrence === 1
          ? `PT-${fingerprint}`
          : `PT-${fingerprint}-${String(occurrence).padStart(2, '0')}`,
    };
  });
}

export function buildRetestFindings(
  sourceFindings: PentestFinding[],
  currentFindings: PentestFinding[],
  runtimeStatus: PentestRuntimeStatus,
  blockedChecks: string[],
): PentestRetestFinding[] {
  return sourceFindings.map((finding) => {
    const retestStatus = evaluateRetestStatus(
      finding,
      currentFindings,
      runtimeStatus,
      blockedChecks,
    );
    return {
      ...finding,
      status: retestStatus,
      retest_status: retestStatus,
    };
  });
}

function evaluateRetestStatus(
  sourceFinding: PentestFinding,
  currentFindings: PentestFinding[],
  runtimeStatus: PentestRuntimeStatus,
  blockedChecks: string[],
): PentestRetestStatus {
  if (
    sourceFinding.runtime_required &&
    (runtimeStatus.status === 'blocked' ||
      runtimeStatus.status === 'not-configured' ||
      blockedChecks.some((entry) => entry.toLowerCase().includes('runtime')))
  ) {
    return 'needs-manual-verification';
  }

  const match = currentFindings.some((current) => {
    if (current.category !== sourceFinding.category) {
      return false;
    }

    if (
      sourceFinding.affected_packages.length > 0 &&
      current.affected_packages.some((pkg) => sourceFinding.affected_packages.includes(pkg))
    ) {
      return true;
    }

    if (
      sourceFinding.affected_modules.length > 0 &&
      current.affected_modules.some((moduleName) =>
        sourceFinding.affected_modules.includes(moduleName),
      )
    ) {
      return true;
    }

    return current.title === sourceFinding.title;
  });

  return match ? 'still-open' : 'fixed';
}

function hasAny(content: string, tokens: string[]): boolean {
  return tokens.some((token) => content.includes(token));
}

function hasAtLeast(content: string, count: number, tokens: string[]): boolean {
  return tokens.filter((token) => content.includes(token)).length >= count;
}

function normalizeImpact(content: string): 'high' | 'medium' | 'low' {
  const lowered = content.toLowerCase();
  if (
    lowered.includes('critical') ||
    lowered.includes('remote code execution') ||
    lowered.includes('credential') ||
    lowered.includes('auth bypass')
  ) {
    return 'high';
  }

  if (lowered.includes('moderate') || lowered.includes('medium')) {
    return 'medium';
  }

  return 'low';
}

function findingFingerprint(finding: PentestFinding): string {
  const payload = JSON.stringify({
    title: finding.title,
    description: finding.description,
    impact: finding.impact,
    effort: finding.effort,
    possible_solution_direction: finding.possible_solution_direction,
    how_to_reproduce: [...finding.how_to_reproduce],
    impact_area: [...finding.impact_area].sort(),
    evidence: [...finding.evidence],
    category: finding.category,
    source_types: [...finding.source_types].sort(),
    affected_modules: [...finding.affected_modules].sort(),
    affected_packages: [...finding.affected_packages].sort(),
    runtime_required: finding.runtime_required,
    manual_follow_up: finding.manual_follow_up,
  });

  return createHash('sha1').update(payload).digest('hex').slice(0, 8).toUpperCase();
}

async function readNativeAuditFindings(artifactDir: string): Promise<OsvVulnerabilityRecord[]> {
  const findings: OsvVulnerabilityRecord[] = [];
  const dependencyDir = join(artifactDir, 'dependencies');

  const npmAudit = await readJsonMaybe<{
    vulnerabilities?: Record<
      string,
      {
        name?: string;
        severity?: string;
        via?: Array<
          string | { source?: number | string; title?: string; url?: string; name?: string }
        >;
        range?: string;
      }
    >;
  }>(join(dependencyDir, 'npm-audit.json'));
  for (const [name, vulnerability] of Object.entries(npmAudit?.vulnerabilities ?? {})) {
    const via = (vulnerability.via ?? []).find((entry) => typeof entry === 'object') as
      { source?: number | string; title?: string; url?: string; name?: string } | undefined;
    findings.push({
      package_name: vulnerability.name ?? name,
      ecosystem: 'npm',
      version: vulnerability.range ?? 'unknown',
      advisory_id: String(via?.source ?? `${name}-npm-audit`),
      summary: via?.title ?? `npm audit advisory for ${name}`,
      details: via?.url ?? '',
    });
  }

  const pnpmAudit = await readJsonMaybe<{
    advisories?: Record<
      string,
      {
        module_name?: string;
        severity?: string;
        title?: string;
        cves?: string[];
        overview?: string;
      }
    >;
  }>(join(dependencyDir, 'pnpm-audit.json'));
  for (const advisory of Object.values(pnpmAudit?.advisories ?? {})) {
    findings.push({
      package_name: advisory.module_name ?? 'unknown',
      ecosystem: 'npm',
      version: 'unknown',
      advisory_id: advisory.cves?.[0] ?? `${advisory.module_name ?? 'unknown'}-pnpm-audit`,
      summary: advisory.title ?? `pnpm audit advisory for ${advisory.module_name ?? 'unknown'}`,
      details: advisory.overview ?? '',
    });
  }

  const composerAudit = await readJsonMaybe<{
    advisories?:
      | Record<
          string,
          Array<{ advisoryId?: string; title?: string; link?: string; affectedVersions?: string }>
        >
      | Array<{ packageName?: string; advisoryId?: string; title?: string; link?: string }>;
  }>(join(dependencyDir, 'composer-audit.json'));
  if (Array.isArray(composerAudit?.advisories)) {
    for (const advisory of composerAudit.advisories) {
      findings.push({
        package_name: advisory.packageName ?? 'unknown',
        ecosystem: 'Packagist',
        version: 'unknown',
        advisory_id: advisory.advisoryId ?? `${advisory.packageName ?? 'unknown'}-composer-audit`,
        summary:
          advisory.title ?? `composer audit advisory for ${advisory.packageName ?? 'unknown'}`,
        details: advisory.link ?? '',
      });
    }
  } else {
    for (const [packageName, advisories] of Object.entries(composerAudit?.advisories ?? {})) {
      for (const advisory of advisories) {
        findings.push({
          package_name: packageName,
          ecosystem: 'Packagist',
          version: advisory.affectedVersions ?? 'unknown',
          advisory_id: advisory.advisoryId ?? `${packageName}-composer-audit`,
          summary: advisory.title ?? `composer audit advisory for ${packageName}`,
          details: advisory.link ?? '',
        });
      }
    }
  }

  const seen = new Set<string>();
  return findings.filter((finding) => {
    const key = `${finding.package_name}:${finding.advisory_id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

async function readJsonMaybe<T>(path: string): Promise<T | null> {
  if (!existsSync(path)) {
    return null;
  }

  try {
    return JSON.parse(await readFile(path, 'utf8')) as T;
  } catch {
    return null;
  }
}
